import { useState } from "react";
import { readerPages } from "@/mocks/home";

export default function ReaderPreview() {
  const [page, setPage] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [night, setNight] = useState(false);

  const current = readerPages[page];
  const total = readerPages.length;

  const prev = () => setPage((p) => Math.max(0, p - 1));
  const next = () => setPage((p) => Math.min(total - 1, p + 1));
  const changeZoom = (d: number) => setZoom((z) => Math.min(2.4, Math.max(0.6, z + d)));

  return (
    <section id="reader" className="scroll-mt-20 bg-background-950 py-16 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="mx-auto max-w-2xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-background-800 px-4 py-1.5 text-xs font-medium text-primary-400">
            <i className="ri-arrow-right-s-line"></i>
            真实阅读体验预览
          </span>
          <h2 className="mt-4 font-heading text-2xl font-bold text-background-50 md:text-4xl">
            亲手体验韩漫在线的阅读器
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-background-400 md:text-base">
            点击翻页、拖动缩放、一键切换夜间模式，感受无弹窗高清阅读的流畅顺滑。
          </p>
        </div>

        <div className="mx-auto mt-12 max-w-4xl">
          <div className="overflow-hidden rounded-xl border border-background-800">
            {/* Reader toolbar */}
            <div className="flex flex-wrap items-center justify-between gap-3 bg-background-900 px-4 py-3">
              <div className="flex items-center gap-2 text-sm text-background-300">
                <span className="text-background-200">第 12 话</span>
                <span className="text-background-600">·</span>
                <span className="truncate">月落星沉 · 星钥篇</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setNight((n) => !n)}
                  className="flex h-9 items-center gap-1.5 rounded-full bg-background-800 px-3 text-xs text-background-200 transition-colors hover:bg-background-700"
                  aria-label="切换夜间模式"
                >
                  <i className={night ? "ri-sun-line" : "ri-moon-clear-line"}></i>
                  {night ? "日间" : "夜间"}
                </button>
                <button
                  type="button"
                  onClick={() => changeZoom(-0.2)}
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-background-800 text-background-200 transition-colors hover:bg-background-700"
                  aria-label="缩小"
                >
                  <i className="ri-zoom-out-line"></i>
                </button>
                <span className="w-12 text-center text-xs text-background-300">
                  {Math.round(zoom * 100)}%
                </span>
                <button
                  type="button"
                  onClick={() => changeZoom(0.2)}
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-background-800 text-background-200 transition-colors hover:bg-background-700"
                  aria-label="放大"
                >
                  <i className="ri-zoom-in-line"></i>
                </button>
                <button
                  type="button"
                  onClick={() => setZoom(1)}
                  className="flex h-9 items-center gap-1 rounded-full bg-background-800 px-3 text-xs text-background-200 transition-colors hover:bg-background-700"
                  aria-label="重置缩放"
                >
                  <i className="ri-restart-line"></i>
                  重置
                </button>
              </div>
            </div>

            {/* Canvas */}
            <div
              className={`relative flex h-[440px] items-center justify-center overflow-hidden md:h-[520px] ${
                night ? "bg-background-950" : "bg-background-200"
              }`}
            >
              <div
                className="flex max-h-full overflow-hidden rounded-md transition-transform duration-200"
                style={{ transform: `scale(${zoom})` }}
              >
                <img
                  src={current.cover}
                  alt={`韩漫在线阅读器预览第 ${page + 1} 屏`}
                  className="h-full max-h-full w-auto object-contain"
                />
              </div>

              <button
                type="button"
                onClick={prev}
                disabled={page === 0}
                className="absolute left-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 text-background-50 transition-colors hover:bg-primary-500 disabled:cursor-not-allowed disabled:opacity-30 md:left-5"
                aria-label="上一屏"
              >
                <i className="ri-arrow-left-s-line text-xl"></i>
              </button>
              <button
                type="button"
                onClick={next}
                disabled={page === total - 1}
                className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 text-background-50 transition-colors hover:bg-primary-500 disabled:cursor-not-allowed disabled:opacity-30 md:right-5"
                aria-label="下一屏"
              >
                <i className="ri-arrow-right-s-line text-xl"></i>
              </button>

              <span className="absolute bottom-3 right-4 rounded-full bg-black/50 px-3 py-1 text-xs text-background-100">
                {page + 1} / {total}
              </span>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              {[
                { icon: "ri-window-line", label: "无弹窗" },
                { icon: "ri-arrow-left-right-line", label: "顺滑翻页" },
                { icon: "ri-zoom-in-line", label: "自由缩放" },
              ].map((f) => (
                <span
                  key={f.label}
                  className="flex items-center gap-1.5 text-xs text-background-400"
                >
                  <i className={`${f.icon} text-primary-400`}></i>
                  {f.label}
                </span>
              ))}
            </div>
            <a
              href="#showcase"
              className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-400"
            >
              去阅读更多韩漫
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}